import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';

interface RetireParams {
  id: string;
  actorId?: string | null;
}

/**
 * POST /api/ConsentTemplate/Retire/{id}?actorId= → boolean.
 *
 * Contract: Scrips.Patient `ConsentTemplateController.Retire` (PR #882).
 * Retires a template so it is no longer offered for new consents. Existing
 * signed consents are unaffected (they pin their own versionHash). Destructive
 * to availability — guard the call site with a shadcn AlertDialog.
 *
 * actorId is AUDIT-ONLY (stored as Updatedby) — NOT a trust boundary (Samer
 * review #550). Authorization is `[Authorize]` + the org header. Hardening
 * follow-up: BE should derive the actor from the JWT/UserId header instead of
 * trusting this client param. Tracked for #882.
 */
export function useRetireConsentTemplate() {
  const qc = useQueryClient();
  return useMutation<boolean, Error, RetireParams>({
    mutationFn: ({ id, actorId }) => {
      const qs = actorId ? `?actorId=${encodeURIComponent(actorId)}` : '';
      return post<boolean>(`/api/ConsentTemplate/Retire/${id}${qs}`, {});
    },
    onSuccess: (_ok, { id }) => {
      invalidateIfNotInFlight(qc, ['consent-templates']);
      invalidateIfNotInFlight(qc, ['consent-template', id]);
    },
  });
}
